import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  MapPin, 
  DollarSign, 
  Mail,
  Loader2,
  ChevronDown,
  ChevronUp,
  Filter,
  Info,
  AlertCircle,
  Calculator,
  Globe,
  Package,
  Upload,
  FileSpreadsheet,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback defaults (used if backend fails)
const FALLBACK_ECONOMICS = {
  cost_per_preview_usd: 0.75,
  cost_per_full_site_usd: 0.40,
  default_site_price_usd: 700,
  act_fast_price_usd: 550,
  provider_costs: {
    mock: { usd_per_lead: 0.00 },
    outscraper: { usd_per_lead: 0.00 },
    data_axle: { usd_per_lead: 0.00 }
  }
};

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const STEPS = [
  { id: 1, title: "Campaign Type", icon: Package },
  { id: 2, title: "Location", icon: MapPin },
  { id: 3, title: "Pricing", icon: DollarSign },
  { id: 4, title: "Sender Info", icon: Mail },
];

// Default campaign filters (email-first automation)
const DEFAULT_FILTERS = {
  require_email: true,
  require_no_website: true,
  exclude_directory_websites: true,
  search_radius_km: 25,
  min_rating: null,
  min_reviews: null,
  verification: "any",
  location_type: "any"
};

// Default Stock & Load email template
const STOCK_LOAD_DEFAULT_SUBJECT = "Quick question about van stock at {{company_name}}";
const STOCK_LOAD_DEFAULT_BODY = `Hi {{first_name}},

I'm the founder of Stock & Load — we help service companies keep truck inventory accountable so parts don't "disappear" and reorders don't snowball.

If you're managing multiple trucks, I'm curious: do you have a reliable way to track inventory by truck (not by tech)?

If it's a headache, I'm happy to show what we're doing.

— {{sender_name}}`;

// Default CSV Upload email template
const CSV_UPLOAD_DEFAULT_SUBJECT = "Quick message for {{business_name}}";
const CSV_UPLOAD_DEFAULT_BODY = `Hi there,

I came across {{business_name}} and wanted to reach out about how we help {{industry}} businesses in {{city}}, {{state}}.

Would you be open to a quick conversation?

Best,
{{sender_name}}`;

// Confidence badge colors
const CONFIDENCE_STYLES = {
  high: { bg: "bg-green-100", text: "text-green-700", label: "High Confidence" },
  medium: { bg: "bg-blue-100", text: "text-blue-700", label: "Medium Confidence" },
  low: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Low Confidence" },
  none: { bg: "bg-gray-100", text: "text-gray-500", label: "No Data" }
};

export default function CampaignSetup() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Provider info for capability-aware UI
  const [providerInfo, setProviderInfo] = useState(null);
  const [activeProvider, setActiveProvider] = useState(null);
  
  // Economics config from backend
  const [economics, setEconomics] = useState(FALLBACK_ECONOMICS);
  
  // Industry-scoped stats (updates when category changes)
  const [industryStats, setIndustryStats] = useState(null);
  const [loadingIndustryStats, setLoadingIndustryStats] = useState(false);
  
  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvUploadResult, setCsvUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Brand selection state (Phase 1 - Multi-Brand Foundation)
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  
  const [formData, setFormData] = useState({
    campaign_type: "website", // "website", "stock_load", or "csv_upload"
    brand_id: "", // Required - must be explicitly selected
    category: "",
    city: "",
    state: "",
    max_leads: 25,
    standard_price: 750,
    fast_action_price: 600,
    fast_action_hours: 24,
    hosting_enabled: false,
    hosting_yearly_price: 150,
    hosting_monthly_price: 13,
    sender_name: "",
    sender_email: "",
    physical_address: "",
    // Campaign filters
    filters: { ...DEFAULT_FILTERS },
    // Stock & Load specific fields
    stock_load_email_subject: STOCK_LOAD_DEFAULT_SUBJECT,
    stock_load_email_body: STOCK_LOAD_DEFAULT_BODY,
    // CSV Upload specific fields
    csv_email_subject: CSV_UPLOAD_DEFAULT_SUBJECT,
    csv_email_body: CSV_UPLOAD_DEFAULT_BODY
  });

  useEffect(() => {
    fetchCategories();
    fetchProviderInfo();
    fetchEconomics();
    fetchBrands();
  }, []);

  // Fetch industry stats when category changes
  useEffect(() => {
    if (formData.category) {
      fetchIndustryStats(formData.category);
    } else {
      setIndustryStats(null);
    }
  }, [formData.category]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data.categories);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const res = await axios.get(`${API}/brands`, { withCredentials: true });
      setBrands(res.data.brands || []);
    } catch (error) {
      console.error("Failed to fetch brands:", error);
      setBrands([]);
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchProviderInfo = async () => {
    try {
      const res = await axios.get(`${API}/settings/providers`, { withCredentials: true });
      setProviderInfo(res.data);
      const active = res.data.providers?.find(p => p.is_selected);
      setActiveProvider(active);
    } catch (error) {
      console.error("Failed to fetch provider info:", error);
    }
  };

  const fetchEconomics = async () => {
    try {
      const res = await axios.get(`${API}/economics`);
      setEconomics(res.data);
    } catch (error) {
      console.error("Failed to fetch economics, using defaults:", error);
      // Keep using fallback defaults
    }
  };

  const fetchIndustryStats = async (category) => {
    setLoadingIndustryStats(true);
    try {
      const res = await axios.get(`${API}/industry-stats?industry=${encodeURIComponent(category)}`);
      setIndustryStats(res.data);
    } catch (error) {
      console.error("Failed to fetch industry stats:", error);
      setIndustryStats(null);
    } finally {
      setLoadingIndustryStats(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateFilter = (key, value) => {
    setFormData(prev => ({
      ...prev,
      filters: { ...prev.filters, [key]: value }
    }));
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        // All three campaign types are valid BUT brand_id is required
        if (!formData.brand_id) return false;
        if (formData.campaign_type === "csv_upload") {
          // CSV campaigns need file uploaded
          return csvUploadResult && csvUploadResult.stats.valid_leads > 0;
        }
        return formData.campaign_type === "website" || formData.campaign_type === "stock_load";
      case 2:
        // CSV campaigns skip location step (leads have their own locations)
        if (formData.campaign_type === "csv_upload") return true;
        return formData.category && formData.city && formData.state;
      case 3:
        // Pricing step - only required for website campaigns
        if (formData.campaign_type === "stock_load") return true;
        if (formData.campaign_type === "csv_upload") return true;
        return formData.standard_price > 0 && formData.fast_action_price > 0;
      case 4:
        return formData.sender_name && formData.sender_email && formData.physical_address;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid(currentStep)) {
      // CSV campaigns skip step 2 (location)
      if (formData.campaign_type === "csv_upload" && currentStep === 1) {
        setCurrentStep(3); // Skip to email template step
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    // CSV campaigns skip step 2 (location)
    if (formData.campaign_type === "csv_upload" && currentStep === 3) {
      setCurrentStep(1); // Go back to campaign type step
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isStepValid(4)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      if (formData.campaign_type === "csv_upload") {
        // For CSV campaigns, the campaign was already created during file upload
        // Just start it
        const campaignId = csvUploadResult.campaign_id;
        
        // Update campaign with email template
        await axios.patch(`${API}/campaigns/${campaignId}`, {
          csv_email_subject: formData.csv_email_subject,
          csv_email_body: formData.csv_email_body,
          sender_name: formData.sender_name,
          sender_email: formData.sender_email,
          physical_address: formData.physical_address
        }, { withCredentials: true });
        
        // Start campaign
        await axios.post(`${API}/campaigns/${campaignId}/start`, {}, { withCredentials: true });
        
        toast.success(`CSV Upload campaign started! Sending ${csvUploadResult.stats.valid_leads} emails.`);
        navigate("/admin");
      } else {
        // Create campaign with filters
        const res = await axios.post(`${API}/campaigns`, formData, { withCredentials: true });
        const campaignId = res.data.campaign_id;
        
        // Start campaign
        await axios.post(`${API}/campaigns/${campaignId}/start`, {}, { withCredentials: true });
        
        const campaignTypeLabel = formData.campaign_type === "stock_load" ? "Stock & Load" : "Website";
        toast.success(`${campaignTypeLabel} campaign started! Processing leads in background.`);
        navigate("/admin");
      }
    } catch (error) {
      console.error("Failed to create campaign:", error);
      toast.error("Failed to start campaign. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // CSV file upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error("Please select a CSV file");
        return;
      }
      setCsvFile(file);
      setCsvUploadResult(null);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file first");
      return;
    }

    setCsvUploading(true);
    try {
      // First, create the campaign
      const campaignRes = await axios.post(`${API}/campaigns`, {
        ...formData,
        campaign_type: "csv_upload",
        category: "CSV Upload",
        city: "Various",
        state: "Various"
      }, { withCredentials: true });
      
      const campaignId = campaignRes.data.campaign_id;

      // Then upload the CSV
      const formDataUpload = new FormData();
      formDataUpload.append('file', csvFile);

      const uploadRes = await axios.post(
        `${API}/campaigns/${campaignId}/upload-csv`,
        formDataUpload,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setCsvUploadResult(uploadRes.data);
      toast.success(`CSV uploaded! ${uploadRes.data.stats.valid_leads} valid leads found.`);
    } catch (error) {
      console.error("Failed to upload CSV:", error);
      const errorMsg = error.response?.data?.detail || "Failed to upload CSV";
      toast.error(errorMsg);
    } finally {
      setCsvUploading(false);
    }
  };

  const clearCsvFile = () => {
    setCsvFile(null);
    setCsvUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Check if provider supports a capability
  const providerSupports = (capability) => {
    if (!activeProvider?.capabilities) return false;
    return activeProvider.capabilities[capability] === true;
  };

  return (
    <div data-testid="campaign-setup-page">
      <div className="page-header">
        <h1 className="page-title">New Campaign</h1>
        <p className="page-subtitle">Set up a new lead generation and website preview campaign</p>
      </div>
      
      <div className="page-content">
        <div className="wizard-container">
          {/* Progress Steps */}
          <div className="wizard-progress">
            {STEPS.map((step, idx) => (
              <div key={step.id} className={`wizard-step ${currentStep === step.id ? 'active' : ''}`}>
                <div className={`wizard-step-number ${
                  currentStep > step.id ? 'wizard-step-complete' :
                  currentStep === step.id ? 'wizard-step-active' :
                  'wizard-step-pending'
                }`}>
                  {currentStep > step.id ? <Check size={16} /> : step.id}
                </div>
                <span className="wizard-step-label">{step.title}</span>
              </div>
            ))}
          </div>

          {/* Step Content */}
          <Card>
            <CardContent className="p-8">
              {/* Step 1: Campaign Type Selection */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-fade-in" data-testid="step-campaign-type">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">Choose Campaign Type</h2>
                    <p className="text-sm text-muted-foreground">Select the type of outreach campaign you want to run</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Website Campaign Option */}
                    <div 
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.campaign_type === "website" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-muted-foreground"
                      }`}
                      onClick={() => { updateField("campaign_type", "website"); clearCsvFile(); }}
                      data-testid="campaign-type-website"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${formData.campaign_type === "website" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                          <Globe size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold">Website Campaign</h3>
                          <Badge variant="secondary" className="mt-1">Default</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Scrape leads → Generate previews → Send emails
                      </p>
                      <ul className="mt-3 text-sm text-muted-foreground space-y-1">
                        <li>• AI-generated homepage</li>
                        <li>• Website package offer</li>
                        <li>• Fast-action pricing</li>
                      </ul>
                    </div>
                    
                    {/* Stock & Load Campaign Option */}
                    <div 
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.campaign_type === "stock_load" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-muted-foreground"
                      }`}
                      onClick={() => { updateField("campaign_type", "stock_load"); clearCsvFile(); }}
                      data-testid="campaign-type-stock-load"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${formData.campaign_type === "stock_load" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                          <Package size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold">Stock & Load</h3>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Scrape leads → Send direct emails (NO preview)
                      </p>
                      <ul className="mt-3 text-sm text-muted-foreground space-y-1">
                        <li>• Simple text email</li>
                        <li>• Inventory pitch</li>
                        <li>• Fast execution</li>
                      </ul>
                    </div>
                    
                    {/* CSV Upload Campaign Option */}
                    <div 
                      className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.campaign_type === "csv_upload" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-muted-foreground"
                      }`}
                      onClick={() => updateField("campaign_type", "csv_upload")}
                      data-testid="campaign-type-csv-upload"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${formData.campaign_type === "csv_upload" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                          <FileSpreadsheet size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold">CSV Upload</h3>
                          <Badge variant="outline" className="mt-1 bg-green-50 text-green-700 border-green-200">$0 leads</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Upload your own leads → Send custom emails
                      </p>
                      <ul className="mt-3 text-sm text-muted-foreground space-y-1">
                        <li>• Bring your own data</li>
                        <li>• No scraping costs</li>
                        <li>• Custom email template</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* CSV Upload Section */}
                  {formData.campaign_type === "csv_upload" && (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Info size={18} className="text-green-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-green-800">CSV Upload Campaign</p>
                            <p className="text-green-700">Upload a CSV file with your leads. Required columns: <code className="bg-green-100 px-1 rounded">industry</code>, <code className="bg-green-100 px-1 rounded">business_name</code>, <code className="bg-green-100 px-1 rounded">city</code>, <code className="bg-green-100 px-1 rounded">state</code>, <code className="bg-green-100 px-1 rounded">email</code></p>
                          </div>
                        </div>
                      </div>
                      
                      {/* File Upload Area */}
                      <div className="border-2 border-dashed border-border rounded-lg p-6">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="csv-upload"
                        />
                        
                        {!csvFile ? (
                          <label 
                            htmlFor="csv-upload"
                            className="flex flex-col items-center justify-center cursor-pointer"
                          >
                            <Upload size={40} className="text-muted-foreground mb-2" />
                            <p className="font-medium">Click to upload CSV</p>
                            <p className="text-sm text-muted-foreground">Max 5,000 rows</p>
                          </label>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileSpreadsheet size={24} className="text-green-600" />
                                <div>
                                  <p className="font-medium">{csvFile.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {(csvFile.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearCsvFile}
                              >
                                <X size={16} />
                              </Button>
                            </div>
                            
                            {!csvUploadResult && (
                              <Button 
                                onClick={handleCsvUpload}
                                disabled={csvUploading}
                                className="w-full"
                              >
                                {csvUploading ? (
                                  <>
                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <Upload size={16} className="mr-2" />
                                    Upload & Validate
                                  </>
                                )}
                              </Button>
                            )}
                            
                            {csvUploadResult && (
                              <div className="p-4 bg-muted rounded-lg">
                                <h4 className="font-medium text-green-700 mb-2">✓ CSV Processed Successfully</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div>Total rows:</div>
                                  <div className="font-medium">{csvUploadResult.stats.total_rows}</div>
                                  <div>Valid leads:</div>
                                  <div className="font-medium text-green-600">{csvUploadResult.stats.valid_leads}</div>
                                  <div>Skipped (no email):</div>
                                  <div className="font-medium text-amber-600">
                                    {csvUploadResult.stats.skipped_no_email + csvUploadResult.stats.skipped_invalid_email}
                                  </div>
                                  <div>Skipped (duplicate):</div>
                                  <div className="font-medium text-amber-600">{csvUploadResult.stats.skipped_duplicate}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Brand Selection - Required for all campaign types */}
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="brand_id" className="text-base font-medium">Select Brand *</Label>
                      <p className="text-sm text-muted-foreground mt-1">Choose which brand this campaign will use for sender identity and email content</p>
                    </div>
                    
                    {loadingBrands ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 size={16} className="animate-spin" />
                        <span className="text-sm">Loading brands...</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {brands.filter(b => b.is_active).map((brand) => {
                          const isSelected = formData.brand_id === brand.brand_id;
                          const hasSenderConfig = brand.sender_email && brand.sender_name;
                          
                          return (
                            <div 
                              key={brand.brand_id}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected 
                                  ? "border-primary bg-primary/5" 
                                  : "border-border hover:border-muted-foreground"
                              }`}
                              onClick={() => updateField("brand_id", brand.brand_id)}
                              data-testid={`brand-option-${brand.slug}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold">{brand.name}</h4>
                                {isSelected && (
                                  <Check size={18} className="text-primary" />
                                )}
                              </div>
                              {brand.tagline && (
                                <p className="text-sm text-muted-foreground mb-2">{brand.tagline}</p>
                              )}
                              <div className="flex items-center gap-2">
                                {hasSenderConfig ? (
                                  <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
                                    Sender configured
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200">
                                    Sender not configured
                                  </Badge>
                                )}
                              </div>
                              {!hasSenderConfig && isSelected && (
                                <div className="mt-3 p-2 bg-amber-50 rounded text-xs text-amber-700">
                                  <AlertCircle size={12} className="inline mr-1" />
                                  Campaigns will run in mock/test mode until sender is configured
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {brands.filter(b => b.is_active).length === 0 && (
                          <div className="col-span-2 p-4 text-center text-muted-foreground bg-muted rounded-lg">
                            No brands available. Please contact admin to configure brands.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {formData.campaign_type === "stock_load" && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Info size={18} className="text-blue-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-800">Stock & Load Campaign</p>
                          <p className="text-blue-700">This campaign type skips website preview generation and sends a simple outreach email about inventory management.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Step 2: Location & Category */}
              {currentStep === 2 && (
                <div className="space-y-6 animate-fade-in" data-testid="step-location">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-semibold mb-1">Target Location & Category</h2>
                      <p className="text-sm text-muted-foreground">Define where and what type of businesses to target</p>
                    </div>
                    {/* Active Provider Badge */}
                    {activeProvider && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm">
                        <span className="text-muted-foreground">Provider:</span>
                        <span className="font-medium">{activeProvider.display_name}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <Label htmlFor="category">Business Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => updateField("category", v)}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <Label htmlFor="city">City *</Label>
                      <Input 
                        id="city"
                        data-testid="city-input"
                        value={formData.city}
                        onChange={(e) => updateField("city", e.target.value)}
                        placeholder="e.g., Austin"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="state">State *</Label>
                      <Select value={formData.state} onValueChange={(v) => updateField("state", v)}>
                        <SelectTrigger data-testid="state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="max_leads">Maximum Leads</Label>
                    <Input 
                      id="max_leads"
                      data-testid="max-leads-input"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.max_leads}
                      onChange={(e) => updateField("max_leads", parseInt(e.target.value) || 25)}
                    />
                    <p className="form-hint">Number of businesses to target (1-100)</p>
                  </div>

                  {/* Campaign Economics Panel - Provider-Aware, Industry-Scoped */}
                  <div className="p-4 bg-muted/50 border border-border rounded-lg space-y-4" data-testid="campaign-economics">
                    {/* Header with Confidence Badge */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calculator size={16} className="text-muted-foreground" />
                        <span className="text-sm font-medium">Campaign Cost & Break-Even</span>
                      </div>
                      {formData.category && industryStats && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <span className={`px-2 py-1 text-xs rounded-full ${CONFIDENCE_STYLES[industryStats.confidence || 'none'].bg} ${CONFIDENCE_STYLES[industryStats.confidence || 'none'].text}`}>
                                {CONFIDENCE_STYLES[industryStats.confidence || 'none'].label}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-xs">{industryStats.confidence_reason}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>

                    {/* Cost Section */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Cost per preview</p>
                        <p className="font-medium">
                          ${(economics.cost_per_preview_usd + (economics.provider_costs?.[activeProvider?.name]?.usd_per_lead || 0)).toFixed(2)}
                          {activeProvider?.name && economics.provider_costs?.[activeProvider.name]?.usd_per_lead > 0 && (
                            <span className="text-xs text-muted-foreground ml-1">
                              (incl. {activeProvider.display_name})
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Est. preview spend ({formData.max_leads} leads)</p>
                        <p className="font-medium">
                          ${((economics.cost_per_preview_usd + (economics.provider_costs?.[activeProvider?.name]?.usd_per_lead || 0)) * formData.max_leads).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Industry-Scoped Projections */}
                    {formData.category && (
                      <div className="pt-3 border-t border-border">
                        {loadingIndustryStats ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 size={14} className="animate-spin" />
                            Loading industry data...
                          </div>
                        ) : industryStats ? (
                          <>
                            {industryStats.has_sufficient_data ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground text-xs">Projected wins</p>
                                    <p className="font-medium">
                                      {Math.round(formData.max_leads * industryStats.conversion_rate)}
                                      <span className="text-xs text-muted-foreground ml-1">
                                        ({(industryStats.conversion_rate * 100).toFixed(1)}% conv.)
                                      </span>
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-xs">Projected revenue</p>
                                    <p className="font-medium text-green-600">
                                      ${Math.round(formData.max_leads * industryStats.conversion_rate * (formData.standard_price || economics.default_site_price_usd)).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Act-fast: ${Math.round(formData.max_leads * industryStats.conversion_rate * (formData.fast_action_price || economics.act_fast_price_usd)).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground text-xs">Break-even (normal price)</p>
                                    <p className="font-medium">
                                      {Math.ceil(((economics.cost_per_preview_usd + (economics.provider_costs?.[activeProvider?.name]?.usd_per_lead || 0)) * formData.max_leads) / (formData.standard_price || economics.default_site_price_usd))} conversions
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-xs">Break-even (act-fast)</p>
                                    <p className="font-medium">
                                      {Math.ceil(((economics.cost_per_preview_usd + (economics.provider_costs?.[activeProvider?.name]?.usd_per_lead || 0)) * formData.max_leads) / (formData.fast_action_price || economics.act_fast_price_usd))} conversions
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm">
                                <p className="text-amber-600 font-medium flex items-center gap-1">
                                  <AlertCircle size={14} />
                                  Insufficient data — early-stage industry
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {industryStats.leads > 0 
                                    ? `Only ${industryStats.leads} historical leads and ${industryStats.wins} wins for ${formData.category}.`
                                    : `No historical data for ${formData.category} yet.`
                                  }
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Conversion projections unavailable. Consider conservative estimates (0.5%–1.0%).
                                </p>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">Select a category to see industry projections</p>
                        )}
                      </div>
                    )}

                    {/* Footer note */}
                    <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                      <p>+ ${economics.cost_per_full_site_usd.toFixed(2)} per full site build (only when marked won)</p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger className="text-xs underline decoration-dotted cursor-help">
                            How are estimates calculated?
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-xs">
                            <p className="text-xs">Estimates use industry-only historical data and configured provider costs. Projections are scoped strictly to {formData.category || 'the selected industry'} — no cross-industry blending.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  {/* Advanced Filters Section */}
                  <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="outline" className="w-full justify-between" data-testid="advanced-filters-toggle">
                        <div className="flex items-center gap-2">
                          <Filter size={16} />
                          <span>Advanced Filters</span>
                          <span className="text-xs text-muted-foreground">(optional)</span>
                        </div>
                        {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4 space-y-6 p-4 border border-border rounded-lg bg-muted/30">
                      
                      {/* Email-First Info Banner */}
                      <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <Info size={18} className="text-blue-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-800">Email-First Automation</p>
                          <p className="text-blue-700">Leads without email addresses will be automatically skipped. No phone-only workflow.</p>
                        </div>
                      </div>

                      {/* Core Filters */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm">Require Email</Label>
                            <p className="text-xs text-muted-foreground">Only use leads with email addresses</p>
                          </div>
                          <Switch 
                            checked={formData.filters.require_email}
                            onCheckedChange={(v) => updateFilter("require_email", v)}
                            disabled={true} // Always ON for email-first
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm">No Website Only</Label>
                            <p className="text-xs text-muted-foreground">Target businesses without a real website</p>
                          </div>
                          <Switch 
                            checked={formData.filters.require_no_website}
                            onCheckedChange={(v) => updateFilter("require_no_website", v)}
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm">Treat Directory Links as &quot;No Website&quot;</Label>
                            <p className="text-xs text-muted-foreground">Facebook, Yelp, etc. don&apos;t count as real websites</p>
                          </div>
                          <Switch 
                            checked={formData.filters.exclude_directory_websites}
                            onCheckedChange={(v) => updateFilter("exclude_directory_websites", v)}
                          />
                        </div>
                      </div>

                      {/* Quality Filters */}
                      <div className="space-y-4 pt-4 border-t border-border">
                        <p className="text-sm font-medium text-muted-foreground">Quality Filters (Optional)</p>
                        
                        {/* Min Rating */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Minimum Rating</Label>
                            <span className="text-sm font-medium">
                              {formData.filters.min_rating ? `${formData.filters.min_rating}+` : "Any"}
                            </span>
                          </div>
                          <Slider
                            value={[formData.filters.min_rating || 0]}
                            onValueChange={([v]) => updateFilter("min_rating", v === 0 ? null : v)}
                            min={0}
                            max={5}
                            step={0.5}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Any</span>
                            <span>5 ★</span>
                          </div>
                          {!providerSupports('supports_rating_filter') && activeProvider?.name !== 'mock' && (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                              <AlertCircle size={12} />
                              Applied after leads are fetched
                            </p>
                          )}
                        </div>

                        {/* Min Reviews */}
                        <div className="form-group">
                          <Label className="text-sm">Minimum Reviews</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.filters.min_reviews || ""}
                            onChange={(e) => updateFilter("min_reviews", e.target.value ? parseInt(e.target.value) : null)}
                            placeholder="Any"
                          />
                          {!providerSupports('supports_reviews_filter') && activeProvider?.name !== 'mock' && (
                            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                              <AlertCircle size={12} />
                              Applied after leads are fetched
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Provider-specific note */}
                      {activeProvider && activeProvider.capabilities?.requires_post_filtering && (
                        <div className="text-xs text-muted-foreground p-2 bg-muted rounded">
                          <strong>Note:</strong> Some filters will be applied after leads are fetched from {activeProvider.display_name}.
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}

              {/* Step 3: Pricing (only for Website campaigns) */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-fade-in" data-testid="step-pricing">
                  {formData.campaign_type === "website" ? (
                    <>
                      <div>
                        <h2 className="text-xl font-semibold mb-1">Pricing Configuration</h2>
                        <p className="text-sm text-muted-foreground">Set your website pricing and fast-action offer</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <Label htmlFor="standard_price">Standard Price ($) *</Label>
                      <Input 
                        id="standard_price"
                        data-testid="standard-price-input"
                        type="number"
                        min="0"
                        value={formData.standard_price}
                        onChange={(e) => updateField("standard_price", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="fast_action_price">Fast-Action Price ($) *</Label>
                      <Input 
                        id="fast_action_price"
                        data-testid="fast-action-price-input"
                        type="number"
                        min="0"
                        value={formData.fast_action_price}
                        onChange={(e) => updateField("fast_action_price", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="fast_action_hours">Fast-Action Window (hours)</Label>
                    <Input 
                      id="fast_action_hours"
                      data-testid="fast-action-hours-input"
                      type="number"
                      min="1"
                      max="72"
                      value={formData.fast_action_hours}
                      onChange={(e) => updateField("fast_action_hours", parseInt(e.target.value) || 24)}
                    />
                    <p className="form-hint">Hours before the fast-action discount expires</p>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Label className="text-base">Hosting Upsell</Label>
                        <p className="text-sm text-muted-foreground">Include hosting service offer in emails</p>
                      </div>
                      <Switch 
                        data-testid="hosting-toggle"
                        checked={formData.hosting_enabled}
                        onCheckedChange={(v) => updateField("hosting_enabled", v)}
                      />
                    </div>
                    
                    {formData.hosting_enabled && (
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border mt-4">
                        <div className="form-group">
                          <Label htmlFor="hosting_yearly_price">Yearly Hosting ($)</Label>
                          <Input 
                            id="hosting_yearly_price"
                            data-testid="hosting-yearly-price-input"
                            type="number"
                            min="0"
                            value={formData.hosting_yearly_price}
                            onChange={(e) => updateField("hosting_yearly_price", parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="form-group">
                          <Label htmlFor="hosting_monthly_price">Monthly Hosting ($)</Label>
                          <Input 
                            id="hosting_monthly_price"
                            data-testid="hosting-monthly-price-input"
                            type="number"
                            min="0"
                            value={formData.hosting_monthly_price}
                            onChange={(e) => updateField("hosting_monthly_price", parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                    </>
                  ) : formData.campaign_type === "csv_upload" ? (
                    /* CSV Upload Email Template Editor */
                    <>
                      <div>
                        <h2 className="text-xl font-semibold mb-1">CSV Upload Email Template</h2>
                        <p className="text-sm text-muted-foreground">Customize the outreach email for your uploaded leads</p>
                      </div>
                      
                      {/* Upload Summary */}
                      {csvUploadResult && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                          <div className="flex items-start gap-3">
                            <FileSpreadsheet size={18} className="text-green-600 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-green-800">{csvUploadResult.stats.valid_leads} leads ready to email</p>
                              <p className="text-green-700">From: {csvUploadResult.filename}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                        <div className="flex items-start gap-3">
                          <Info size={18} className="text-blue-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-blue-800">Available placeholders:</p>
                            <p className="text-blue-700 font-mono text-xs mt-1">
                              {"{{business_name}}"} {"{{city}}"} {"{{state}}"} {"{{industry}}"} {"{{sender_name}}"}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <Label htmlFor="csv_email_subject">Email Subject *</Label>
                        <Input 
                          id="csv_email_subject"
                          data-testid="csv-email-subject-input"
                          value={formData.csv_email_subject}
                          onChange={(e) => updateField("csv_email_subject", e.target.value)}
                          placeholder="Quick message for {{business_name}}"
                        />
                      </div>

                      <div className="form-group">
                        <Label htmlFor="csv_email_body">Email Body *</Label>
                        <Textarea 
                          id="csv_email_body"
                          data-testid="csv-email-body-input"
                          value={formData.csv_email_body}
                          onChange={(e) => updateField("csv_email_body", e.target.value)}
                          rows={12}
                          className="font-mono text-sm"
                        />
                        <p className="form-hint">Plain text email - keep it simple and personal</p>
                      </div>
                      
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          updateField("csv_email_subject", CSV_UPLOAD_DEFAULT_SUBJECT);
                          updateField("csv_email_body", CSV_UPLOAD_DEFAULT_BODY);
                        }}
                      >
                        Reset to Default Template
                      </Button>
                    </>
                  ) : (
                    /* Stock & Load Email Template Editor */
                    <>
                      <div>
                        <h2 className="text-xl font-semibold mb-1">Stock & Load Email Template</h2>
                        <p className="text-sm text-muted-foreground">Customize the outreach email for Stock & Load campaign</p>
                      </div>
                      
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                        <div className="flex items-start gap-3">
                          <Info size={18} className="text-blue-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-blue-800">Available placeholders:</p>
                            <p className="text-blue-700 font-mono text-xs mt-1">
                              {"{{first_name}}"} {"{{company_name}}"} {"{{sender_name}}"} {"{{city}}"} {"{{state}}"}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <Label htmlFor="stock_load_email_subject">Email Subject *</Label>
                        <Input 
                          id="stock_load_email_subject"
                          data-testid="stock-load-subject-input"
                          value={formData.stock_load_email_subject}
                          onChange={(e) => updateField("stock_load_email_subject", e.target.value)}
                          placeholder="Quick question about van stock at {{company_name}}"
                        />
                      </div>

                      <div className="form-group">
                        <Label htmlFor="stock_load_email_body">Email Body *</Label>
                        <Textarea 
                          id="stock_load_email_body"
                          data-testid="stock-load-body-input"
                          value={formData.stock_load_email_body}
                          onChange={(e) => updateField("stock_load_email_body", e.target.value)}
                          rows={12}
                          className="font-mono text-sm"
                        />
                        <p className="form-hint">Plain text email - keep it simple and personal</p>
                      </div>
                      
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          updateField("stock_load_email_subject", STOCK_LOAD_DEFAULT_SUBJECT);
                          updateField("stock_load_email_body", STOCK_LOAD_DEFAULT_BODY);
                        }}
                      >
                        Reset to Default Template
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Step 4: Sender Info */}
              {currentStep === 4 && (
                <div className="space-y-6 animate-fade-in" data-testid="step-sender">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">Sender Information</h2>
                    <p className="text-sm text-muted-foreground">Configure your email sender details for outreach</p>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="sender_name">Sender Name *</Label>
                    <Input 
                      id="sender_name"
                      data-testid="sender-name-input"
                      value={formData.sender_name}
                      onChange={(e) => updateField("sender_name", e.target.value)}
                      placeholder="e.g., John Smith"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="sender_email">Sender Email *</Label>
                    <Input 
                      id="sender_email"
                      data-testid="sender-email-input"
                      type="email"
                      value={formData.sender_email}
                      onChange={(e) => updateField("sender_email", e.target.value)}
                      placeholder="e.g., john@invigordigital.com"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="physical_address">Physical Address *</Label>
                    <Input 
                      id="physical_address"
                      data-testid="physical-address-input"
                      value={formData.physical_address}
                      onChange={(e) => updateField("physical_address", e.target.value)}
                      placeholder="e.g., 123 Main St, Austin, TX 78701"
                    />
                    <p className="form-hint">Required for CAN-SPAM compliance</p>
                  </div>

                  {/* Campaign Type Summary */}
                  <div className={`flex items-start gap-3 p-3 rounded-lg ${
                    formData.campaign_type === "stock_load" 
                      ? "bg-blue-50 border border-blue-200" 
                      : "bg-green-50 border border-green-200"
                  }`}>
                    {formData.campaign_type === "stock_load" ? (
                      <>
                        <Package size={18} className="text-blue-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-800">Stock & Load Campaign</p>
                          <p className="text-blue-700">Direct email outreach - no website previews will be generated.</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Check size={18} className="text-green-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-green-800">Website Campaign</p>
                          <p className="text-green-700">Website previews will be generated and sent to leads.</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-border">
                {currentStep > 1 ? (
                  <Button 
                    variant="outline" 
                    onClick={handleBack}
                    disabled={submitting}
                    data-testid="back-button"
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Back
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate("/admin")}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                )}

                {currentStep < 4 ? (
                  <Button 
                    onClick={handleNext}
                    disabled={!isStepValid(currentStep)}
                    data-testid="next-button"
                  >
                    Next
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit}
                    disabled={!isStepValid(4) || submitting}
                    data-testid="launch-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Launching...
                      </>
                    ) : (
                      <>
                        Launch Campaign
                        <ArrowRight size={16} className="ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
